import { api, setAccessToken, refreshSession, getAccessToken } from "./client";
import { getDeviceHash } from "@/lib/device-hash";

/**
 * Auth client for bella-admin-api's NestJS auth module (/v1/auth/*).
 * Contract traced in docs/modules/auth.md.
 */

/** Normalised admin identity. Every screen uses this shape, never a raw payload. */
export interface AdminUser {
  id: string;
  email: string;
  /**
   * Free text straight out of `ad_admin.role` — "Admin", "Customer Support",
   * and whatever else the live table holds. NOT an enum.
   * Never write `role === "admin"`; compare case-insensitively, and prefer a
   * server-side check once role guards exist.
   */
  role: string;
  /**
   * `ad_admin.roleid` — the numeric role. Informational: **never branch on
   * it.** What an admin may do comes from `usePermissions()`, which the server
   * decides per admin. Null is normal on tokens minted by the legacy admin API.
   */
  roleId: number | null;
  isActive: boolean;
}

/** POST /v1/auth/login → data */
interface LoginResponse {
  user: {
    id: string;
    email: string;
    role: string;
    /** Absent on an API deployed before role ids; read as null. */
    roleId?: number | null;
    isActive: boolean;
  };
  /** Access-token lifetime in SECONDS (900 by default), not milliseconds. */
  expiresIn: number;
}

/**
 * GET /v1/auth/profile → data
 *
 * This endpoint does no DB read — it echoes the decoded JWT. So it carries
 * both `id` and `userId` (which can differ in type depending on which service
 * minted the token) and has no `isActive`. Role and status here are as of the
 * last login, not as of now.
 */
interface ProfileResponse {
  user: {
    userId?: string | number;
    id?: string | number;
    email?: string;
    role?: string;
    /**
     * Present only on tokens this API minted since role ids were added.
     * Legacy-minted tokens have never carried it.
     */
    roleId?: number | string | null;
    type?: string;
    iat?: number;
    exp?: number;
  };
}

/**
 * Reads the role id off a payload, whatever form it arrived in.
 *
 * `ad_admin.roleid` is a DOUBLE, so it can reach the client as a number or a
 * string. `7 !== "7"` would hide the console from an owner, which is the exact
 * bug the numeric role exists to avoid.
 */
function readRoleId(raw: number | string | null | undefined): number | null {
  if (raw === null || raw === undefined || raw === "") return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

function normaliseProfile(payload: ProfileResponse): AdminUser {
  const raw = payload.user;
  const id = raw.userId ?? raw.id;
  return {
    id: id === undefined ? "" : String(id),
    email: raw.email ?? "",
    role: raw.role ?? "",
    roleId: readRoleId(raw.roleId),
    // The JWT carries no status. Holding a token that the guard accepted
    // implies the account was active at login; a deactivation mid-session is
    // caught by the next 401, not here.
    isActive: true,
  };
}

export async function login(credentials: LoginCredentials): Promise<AdminUser> {
  const data = await api.post<LoginResponse>(
    "/auth/login",
    {
      email: credentials.email,
      password: credentials.password,
      // Always sent. Omitting it makes the API insert an empty-hash row and
      // then refuse every later login with 403 — see lib/device-hash.ts.
      userAgentHash: getDeviceHash(),
      // `Processor_id` is intentionally not sent: only the legacy Electron
      // wrapper ever produced it, and the legacy web app sent null too.
    },
    // A wrong password must reach the form as a 401, not trigger a refresh.
    { skipAuthRefresh: true },
  );

  // The access token arrived in the Authorization response header and the
  // client has already captured it. If it did not, exposedHeaders is missing
  // on the server and nothing downstream will work — fail here, where the
  // cause is obvious, rather than with a mystery 401 on the next request.
  if (getAccessToken() === null) {
    throw new Error(
      "Login succeeded but no access token was readable. The API must send " +
        "Access-Control-Expose-Headers: Authorization (see bella-admin-api " +
        "main.nest.ts) and must be the NestJS entrypoint.",
    );
  }

  // Normalised rather than returned raw: `roleId` is optional on the wire and
  // must land as `null`, not `undefined`, so every screen can read it the same
  // way regardless of which API version answered.
  return { ...data.user, roleId: readRoleId(data.user.roleId) };
}

/** GET /v1/auth/profile — the "me" endpoint. Requires a live access token. */
export async function getProfile(): Promise<AdminUser> {
  const data = await api.get<ProfileResponse>("/auth/profile");
  return normaliseProfile(data);
}

/** Waits before each retry of a boot-time refresh the API could not answer. */
const RESTORE_RETRY_DELAYS_MS = [1_000, 3_000];

/**
 * Rebuilds the session after a reload, when the in-memory token is gone but
 * the httpOnly refresh cookie may still be valid. Returns null when there is
 * no session to restore — the normal case for a first visit.
 *
 * A refresh the API could not answer is retried briefly first: a reload that
 * lands while bella restarts for a deploy, or during a network blip, would
 * otherwise put an admin with a perfectly good session on the login screen.
 */
export async function restoreSession(): Promise<AdminUser | null> {
  let refresh = await refreshSession();
  for (const delay of RESTORE_RETRY_DELAYS_MS) {
    if (refresh.outcome !== "unavailable") break;
    await new Promise((resolve) => setTimeout(resolve, delay));
    refresh = await refreshSession();
  }
  if (refresh.outcome !== "refreshed") return null;
  try {
    return await getProfile();
  } catch {
    setAccessToken(null);
    return null;
  }
}

export async function logout(): Promise<void> {
  try {
    await api.post<null>("/auth/logout", undefined, { skipAuthRefresh: true });
  } catch {
    // A failed logout must never trap someone in the app. The server-side
    // blacklist is best-effort anyway; clearing locally is what matters.
  } finally {
    setAccessToken(null);
  }
}
